import { useCallback } from "react";
import { usePublicCatalog } from "../context/PublicCatalogContext";
import { useUserContext } from "../context/UserContext";
import { useCartContext } from "../context/CartContext";
import { useOrderContext } from "../context/OrderContext";
import { log } from "../utils/logger";

type CatalogFetchOpts = {
    subscriberId?: number | string;
    isPublicView?: boolean;
    uuid?: string;
};

type CatalogFetcher<T> = (opts: CatalogFetchOpts) => Promise<T[] | { data: T[] }>;

type UseCatalogFetchOptions<T> = {
    fetcher: CatalogFetcher<T>;
    onData: (data: T[]) => void;
    setLoading: (loading: boolean) => void;
    setError?: (message: string | null) => void;
    filterData?: (data: T[]) => T[];
    logLabel?: string;
};

const unwrapResponse = <T,>(response: T[] | { data: T[] }): T[] => {
    if (response && typeof response === "object" && "data" in response) {
        return response.data;
    }
    return response as T[];
};

const isMissingUuid = (uuid?: string) => !uuid || uuid === "false";

const useCatalogFetch = <T,>({
    fetcher,
    onData,
    setLoading,
    setError,
    filterData,
    logLabel,
}: UseCatalogFetchOptions<T>) => {
    const { isPublicView, uuid } = usePublicCatalog();
    const { loggedIn } = useUserContext();
    const { cartData, cartLoaded } = useCartContext();
    const { orderData } = useOrderContext();

    const subscriberId = cartData?.subscriber_id || orderData?.recipient;

    const resetPublicView = useCallback(() => {
        // Don't redirect to login - public view should stay public
        // Just log the error, the UI will show no data
        //log("Public catalog fetch failed - staying on current page");
    }, []);

    const reload = useCallback(async () => {
        setError?.(null);

        if (!isPublicView) {
            if (!loggedIn || !cartLoaded) {
                //log(`Skipping ${logLabel ?? "catalog"} fetch: no user or cart.`);
                setLoading(false);
                return;
            }
            if (isMissingUuid(uuid)) {
                //log(`Skipping ${logLabel ?? "catalog"} fetch: missing uuid.`);
                setLoading(false);
                return;
            }
        } else if (isMissingUuid(uuid)) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const response = await fetcher({ subscriberId, isPublicView, uuid });
            const data = unwrapResponse(response);
            log(
                `[Catalog debug] fetched ${logLabel ?? "catalog"}:`,
                Array.isArray(data) ? data.length : typeof data,
                "items",
                Array.isArray(data)
                    ? data.map((item: any) => item?.code ?? item?.id).filter(Boolean)
                    : data
            );
            onData(filterData ? filterData(data) : data);
        } catch (err: any) {
            setError?.(err?.message ?? "Failed to load.");
            if (isPublicView) {
                resetPublicView();
            }
        } finally {
            setLoading(false);
        }
    }, [
        cartLoaded,
        fetcher,
        filterData,
        isPublicView,
        loggedIn,
        logLabel,
        onData,
        resetPublicView,
        setError,
        setLoading,
        subscriberId,
        uuid,
    ]);

    return { reload };
};

export default useCatalogFetch;
